/** Dashboard view component. */

import { fetchDashboardData, fetchDashboardHypeScores } from '@/api/services/dashboard';
import { useDashboardStore, type HypeScoreWindow } from '@/stores/dashboard';
import {
  useFilterStore,
  type MomentumAlertType,
  type MomentumLevelThreshold,
} from '@/stores/filter';
import { showToast } from '@/utils/helpers';
import { logError } from '@/utils/errors';
import { escapeHTML, formatPrice } from '@/utils/formatters';
import { computeCrossSignals, type CrossSignalEntry } from '@/utils/crossSignals';
import { recordBackendFreshness } from '@/utils/socket';
import { perfMark, perfMeasure } from '@/utils/perf';
import type { DashboardResponse } from '@/types/responses';

// Store sort state for each table
const sortState: Record<string, { column: string | null; direction: 'asc' | 'desc' }> = {
  'breakout-table-container': { column: 'velocity_increase', direction: 'desc' },
  'sentiment-shift-table-container': { column: 'sentiment_change', direction: 'desc' },
  'hype-score-table-container': { column: 'hype_score', direction: 'desc' },
};
const PROCESSED_MENTION_TTL_MS = 60 * 60 * 1000;
const MOMENTUM_ALERT_TTL_MS = 30 * 60 * 1000; // 30 minutes
const _processedMentionUrls = new Map<string, number>();

function cleanupProcessedMomentumMentions(): void {
  const cutoff = Date.now() - PROCESSED_MENTION_TTL_MS;
  for (const [key, seenAt] of _processedMentionUrls.entries()) {
    if (seenAt < cutoff) {
      _processedMentionUrls.delete(key);
    }
  }
}

function shouldProcessMomentumMention(uniqueKey: string): boolean {
  if (_processedMentionUrls.has(uniqueKey)) {
    return false;
  }
  _processedMentionUrls.set(uniqueKey, Date.now());
  return true;
}

function renderDashboardErrorState(): void {
  const crossSignalsEl = document.getElementById('cross-signal-container');
  if (crossSignalsEl) {
    crossSignalsEl.innerHTML = '';
    crossSignalsEl.classList.add('hidden');
  }
  const sections = [
    'alerts-container',
    'breakout-table-container',
    'sentiment-shift-table-container',
    'hype-score-table-container',
  ];
  sections.forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.innerHTML = '<p class="text-red-400 text-sm">Failed to load — please retry.</p>';
    }
  });
}

function renderCrossSignalCallout(
  crossSignals: Map<string, CrossSignalEntry[]>,
  bannedTickers: Set<string>
): void {
  const container = document.getElementById('cross-signal-container');
  if (!container) return;

  const entries = [...crossSignals.entries()]
    .filter(([ticker]) => !bannedTickers.has(ticker))
    .sort((a, b) => b[1].length - a[1].length)
    .slice(0, 8);

  if (entries.length === 0) {
    container.innerHTML = '';
    container.classList.add('hidden');
    return;
  }

  container.classList.remove('hidden');
  container.innerHTML = `
    <div class="card p-4 border border-neon-yellow">
      <h2 class="text-xl font-bold mb-1 text-neon-yellow">🎯 High Conviction</h2>
      <p class="text-secondary text-sm mb-3">Tickers firing across multiple indicators at once.</p>
      <div class="space-y-2">
        ${entries.map(([ticker, sigs]) => `
          <div class="flex items-center gap-3 p-2 rounded bg-gray-800/50 cursor-pointer hover:bg-gray-700/50"
               data-ticker="${escapeHTML(ticker)}">
            <span class="font-bold text-neon-cyan text-base w-16 flex-shrink-0">$${escapeHTML(ticker)}</span>
            <div class="flex flex-wrap gap-1.5 flex-1">
              ${sigs.map(sig => `
                <span class="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-200">${escapeHTML(sig.label)}</span>
              `).join('')}
            </div>
            <span class="text-xs text-neon-yellow font-semibold flex-shrink-0">${sigs.length} signals</span>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

export async function renderDashboard(handleTickerClick: (ticker: string) => void): Promise<void> {
  const perfStartMark = `dashboard.render.start.${Date.now()}`;
  const perfEndMark = `dashboard.render.end.${Date.now()}`;
  perfMark(perfStartMark);
  const { isLoading, setLoading, setData, setAllMentions } = useDashboardStore.getState();
  if (isLoading) return;
  
  // Show dashboard view and hide other views
  document.querySelectorAll('.view').forEach(v => {
    v.classList.add('hidden');
    (v as HTMLElement).style.display = 'none';
  });
  
  const dashboardView = document.getElementById('view-dashboard');
  if (dashboardView) {
    dashboardView.classList.remove('hidden');
    dashboardView.style.display = '';
  }
  
  // Hide loader
  const mainLoader = document.getElementById('main-loader');
  if (mainLoader) {
    mainLoader.classList.add('hidden');
    mainLoader.style.display = 'none';
    mainLoader.setAttribute('aria-busy', 'false');
  }
  
  // Update navigation store
  import('@/stores/navigation').then(module => {
    module.useNavigationStore.getState().setCurrentView('dashboard');
  });
  
  // Update active nav link
  document.querySelectorAll('.nav-link, .nav-link-mobile').forEach(link => {
    link.classList.remove('active');
    if (link.getAttribute('data-view') === 'dashboard') {
      link.classList.add('active');
    }
  });
  
  try {
    setLoading(true);
    
    const data = await fetchDashboardData();
    if (data === null) {
      // 304 Not Modified — keep existing DOM and store state
      setLoading(false);
      perfMark(perfEndMark);
      perfMeasure('dashboard.render_total_ms', perfStartMark, perfEndMark);
      return;
    }
    if (!data) {
      renderDashboardErrorState();
      showToast('Failed to fetch dashboard data', true);
      return;
    }
    
    setData(data);
    setAllMentions(data.recent_mentions || []);
    useFilterStore.getState().seedFromServerDefaults(data.alert_ban_list || []);
    recordBackendFreshness(data.current_time, data.last_mention_time);

    const filterState = useFilterStore.getState();
    const velocityBannedTickers = new Set(
      (filterState.velocityBannedTickers || []).map((ticker) => ticker.toUpperCase())
    );
    const momentumBannedTickers = new Set(
      (filterState.momentumBannedTickers || []).map((ticker) => ticker.toUpperCase())
    );
    const combinedBannedTickers = new Set([
      ...Array.from(velocityBannedTickers),
      ...Array.from(momentumBannedTickers),
    ]);
    
    // Render velocity alerts
    renderVelocityAlerts(data.velocity_alerts || [], handleTickerClick, velocityBannedTickers);
    
    // Process momentum detection and render momentum alerts
    renderMomentumAlerts(
      data.recent_mentions || [],
      data.due_diligence_posts || [],
      handleTickerClick,
      momentumBannedTickers,
      combinedBannedTickers,
      filterState.momentumMinLevel,
      new Set(filterState.momentumEnabledTypes)
    );
    
    // Render breakout tickers (will use store data for re-sorting)
    renderBreakoutTickers(data.breakout_tickers || [], handleTickerClick);
    
    // Render sentiment shift (will use store data for re-sorting)
    renderSentimentShift(data.sentiment_shift || [], handleTickerClick);
    
    // Hype score: full dashboard payload is always 24h; other windows refetched from /dashboard/hype_score
    const hypeWindow = useDashboardStore.getState().hypeScoreWindow;
    if (hypeWindow === '24h') {
      renderHypeScore(data.hype_score || [], handleTickerClick);
    } else {
      await applyHypeScoreWindow(hypeWindow, handleTickerClick);
    }
    
    // Render recent mentions (if we add a dedicated section later)
    renderRecentMentions(data.recent_mentions || []);

    const crossSignals = computeCrossSignals(
      data.velocity_alerts || [],
      data.breakout_tickers || [],
      data.sentiment_shift || [],
      data.hype_score || [],
      undefined,
      filterState.crossSignalMinHypeScore
    );
    renderCrossSignalCallout(crossSignals, combinedBannedTickers);
    
  } catch (error) {
    logError(error, 'Dashboard');
    renderDashboardErrorState();
    showToast('Failed to load dashboard', true);
  } finally {
    setLoading(false);
    perfMark(perfEndMark);
    perfMeasure('dashboard.render_total_ms', perfStartMark, perfEndMark);
  }
}

function renderVelocityAlerts(
  alerts: DashboardResponse['velocity_alerts'],
  _handleTickerClick: (ticker: string) => void,
  bannedTickers: Set<string>
): void {
  const container = document.getElementById('alerts-container');
  const windowLabel = document.getElementById('velocity-alerts-window-label');
  if (!container) return;

  const timeframeMinutes = alerts.length > 0 ? Math.max(...alerts.map((a) => a.timeframe_minutes || 60)) : 60;
  if (windowLabel) {
    if (timeframeMinutes === 60) {
      windowLabel.textContent = 'hour';
    } else if (timeframeMinutes < 60) {
      windowLabel.textContent = `${timeframeMinutes} minutes`;
    } else {
      const hours = Math.round((timeframeMinutes / 60) * 10) / 10;
      windowLabel.textContent = `${hours} hours`;
    }
  }

  const filteredAlerts = alerts.filter((alert) => !bannedTickers.has(alert.ticker.toUpperCase()));

  if (filteredAlerts.length === 0) {
    container.innerHTML = '<p class="text-secondary">No new velocity alerts.</p>';
    return;
  }
  
  // Combine alerts by ticker
  const combinedAlerts = new Map<string, {
    ticker: string;
    totalMentions: number;
    alertCount: number;
    mostRecentTimestamp: string;
    timeframeMinutes: number;
  }>();
  
  filteredAlerts.forEach(alert => {
    const ticker = alert.ticker.toUpperCase();
    const existing = combinedAlerts.get(ticker);
    
    if (existing) {
      // Combine: sum mentions, count alerts, get most recent timestamp
      existing.totalMentions += alert.mention_count || 0;
      existing.alertCount += 1;
      const existingTime = new Date(existing.mostRecentTimestamp).getTime();
      const alertTime = new Date(alert.timestamp).getTime();
      if (alertTime > existingTime) {
        existing.mostRecentTimestamp = alert.timestamp;
      }
    } else {
      // First alert for this ticker
      combinedAlerts.set(ticker, {
        ticker,
        totalMentions: alert.mention_count || 0,
        alertCount: 1,
        mostRecentTimestamp: alert.timestamp,
        timeframeMinutes: alert.timeframe_minutes || 60
      });
    }
  });
  
  // Convert to array and sort by most recent timestamp
  const sortedAlerts = Array.from(combinedAlerts.values())
    .sort((a, b) => {
      const timeA = new Date(a.mostRecentTimestamp).getTime();
      const timeB = new Date(b.mostRecentTimestamp).getTime();
      return timeB - timeA; // Most recent first
    });
  
  const recentMentions = useDashboardStore.getState().data?.recent_mentions || [];
  const fifteenMinutesAgo = Date.now() - (15 * 60 * 1000);
  const sentimentBuckets = new Map<string, { sum: number; count: number }>();
  recentMentions.forEach((m) => {
    const ticker = (m.ticker || '').toUpperCase();
    if (!ticker) return;
    const ts = m.timestamp ? new Date(m.timestamp).getTime() : NaN;
    if (!Number.isFinite(ts) || ts < fifteenMinutesAgo) return;
    const prev = sentimentBuckets.get(ticker) || { sum: 0, count: 0 };
    prev.sum += Number(m.sentiment || 0);
    prev.count += 1;
    sentimentBuckets.set(ticker, prev);
  });
  const sentimentForTicker = (ticker: string): { label: string; colorClass: string; avg: number | null } => {
    const bucket = sentimentBuckets.get(ticker.toUpperCase());
    if (!bucket || bucket.count === 0) {
      return { label: 'Neutral', colorClass: 'bg-gray-500', avg: null };
    }
    const avg = bucket.sum / bucket.count;
    if (avg > 0.1) return { label: 'Bullish', colorClass: 'bg-green-500', avg };
    if (avg < -0.1) return { label: 'Bearish', colorClass: 'bg-red-500', avg };
    return { label: 'Neutral', colorClass: 'bg-yellow-500', avg };
  };

  container.innerHTML = sortedAlerts.map(combined => {
    const alertText = combined.alertCount > 1
      ? `${combined.totalMentions} total mentions (${combined.alertCount} alerts) in ${combined.timeframeMinutes} mins!`
      : `${combined.totalMentions} mentions in ${combined.timeframeMinutes} mins!`;
    const sentiment = sentimentForTicker(combined.ticker);
    const sentimentTitle = sentiment.avg == null
      ? 'Sentiment: no recent samples'
      : `Sentiment (15m avg): ${formatSentiment(sentiment.avg)}`;
    
    return `
      <div class="p-3 card border border-neon-pink rounded-md neon-glow-cyan" data-ticker="${escapeHtml(combined.ticker)}">
        <span class="font-bold text-neon-pink">
          [${formatTimestamp(combined.mostRecentTimestamp)}] 
          <span class="cursor-pointer hover:underline" data-ticker="${escapeHtml(combined.ticker)}">$${escapeHtml(combined.ticker)}</span>:
        </span>
        <span class="text-primary">${alertText}</span>
        <span class="ml-2 inline-flex items-center gap-1 text-xs text-secondary" title="${escapeHTML(sentimentTitle)}">
          <span class="inline-block w-2 h-2 rounded-full ${sentiment.colorClass}"></span>
          ${escapeHTML(sentiment.label)}
        </span>
      </div>
    `;
  }).join('');
  
  // Click handlers are now handled globally via event delegation in app.ts
  // No need to add individual handlers here
}

function renderMomentumAlerts(
  recentMentions: DashboardResponse['recent_mentions'],
  ddPosts: DashboardResponse['due_diligence_posts'],
  _handleTickerClick: (ticker: string) => void,
  bannedTickers: Set<string>,
  crossSignalBannedTickers: Set<string>,
  minLevel: MomentumLevelThreshold,
  enabledTypes: Set<MomentumAlertType>
): void {
  const container = document.getElementById('momentum-alerts-container');
  if (!container) return;
  
  // Import momentum detection utilities
  import('@/utils/momentum').then((momentumModule) => {
    const { processMomentumDetection, momentumTracker } = momentumModule;
    // Use the imported MomentumAlert type
    type MomentumAlert = import('@/utils/momentum').MomentumAlert;
    
    // Update DD posts tracker
    ddPosts.forEach(ddPost => {
      if (ddPost.ticker) {
        const timestamp = ddPost.timestamp 
          ? new Date(ddPost.timestamp).getTime() 
          : Date.now();
        momentumTracker.ddPosts.set(ddPost.ticker, {
          timestamp,
          subreddit: ddPost.subreddit || 'unknown',
          karma: ddPost.author_karma || 0
        });
      }
    });
    
    // Process recent mentions for momentum detection
    const detectedMomentumAlerts: MomentumAlert[] = [];
    cleanupProcessedMomentumMentions();
    recentMentions.forEach(mention => {
      const mentionUniqueKey = mention.url
        ? mention.url
        : `${mention.ticker}:${mention.timestamp ?? ''}:${mention.subreddit ?? ''}`;
      if (!shouldProcessMomentumMention(mentionUniqueKey)) {
        return;
      }

      const detection = processMomentumDetection({
        ticker: mention.ticker,
        timestamp: mention.timestamp,
        author_karma: mention.author_karma,
        subreddit: mention.subreddit || null
      });
      
      if (detection) {
        detectedMomentumAlerts.push(detection);
      }
    });

    // Persist momentum detections across re-renders and incremental refreshes.
    useDashboardStore.getState().mergeMomentumAlerts(detectedMomentumAlerts);

    // Remove duplicates and filter out banned tickers from store-backed momentum alerts.
    const uniqueAlerts = new Map<string, MomentumAlert>();
    const levelRank: Record<string, number> = { low: 1, medium: 2, high: 3, critical: 4 };
    const thresholdRank = minLevel === 'all' ? 1 : levelRank[minLevel];

    const cutoff = Date.now() - MOMENTUM_ALERT_TTL_MS;
    const storeMomentumAlerts = Array.from(useDashboardStore.getState().momentumAlerts.values())
      .filter((alert) => alert.timestamp >= cutoff);
    storeMomentumAlerts.forEach(alert => {
      // Skip if ticker is in ban list
      if (bannedTickers.has(alert.ticker.toUpperCase())) {
        return;
      }
      if (!enabledTypes.has(alert.type as MomentumAlertType)) {
        return;
      }
      if ((levelRank[alert.level] || 1) < thresholdRank) {
        return;
      }
      
      const existing = uniqueAlerts.get(alert.ticker);
      if (!existing || alert.timestamp > existing.timestamp) {
        uniqueAlerts.set(alert.ticker, alert);
      }
    });

    const momentumTickers = new Set(Array.from(uniqueAlerts.keys()));
    const { data: storeData } = useDashboardStore.getState();
    if (storeData) {
      const updatedCrossSignals = computeCrossSignals(
        storeData.velocity_alerts || [],
        storeData.breakout_tickers || [],
        storeData.sentiment_shift || [],
        storeData.hype_score || [],
        momentumTickers,
        useFilterStore.getState().crossSignalMinHypeScore
      );
      renderCrossSignalCallout(updatedCrossSignals, crossSignalBannedTickers);
    }
    
    const sortedAlerts = Array.from(uniqueAlerts.values())
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 5);
    
    if (sortedAlerts.length === 0) {
      container.innerHTML = '<p class="text-secondary">No early momentum signals detected.</p>';
      return;
    }
    
    container.innerHTML = sortedAlerts.map(alert => {
      const level = momentumTracker.alertLevels[alert.level as keyof typeof momentumTracker.alertLevels];
      const timestamp = new Date(alert.timestamp).toLocaleTimeString();
      let description = '';
      
      switch (alert.type) {
        case 'gradual_acceleration':
          description = `Mentions ${alert.ratio}x acceleration: ${alert.previousMentions} → ${alert.currentMentions}`;
          break;
        case 'quality_surge':
          description = `Quality surge ${alert.ratio}x: ${alert.recentAvgKarma} avg karma vs ${alert.previousAvgKarma}`;
          break;
        case 'cross_subreddit_spread':
          description = `Spread across ${alert.subredditCount} subreddits: ${alert.subreddits?.join(', ')}`;
          break;
        case 'dd_mention_combo':
          description = `DD post + climbing mentions: ${alert.recentMentions} mentions (${alert.ddPostAge}m after DD)`;
          break;
      }
      
      return `
        <div class="p-3 ${level.color} rounded-md neon-glow-cyan" data-ticker="${escapeHTML(alert.ticker)}">
          <div class="flex items-center justify-between">
            <div>
              <span class="font-bold ${level.textColor}">${level.icon} [${escapeHTML(timestamp)}] 
                <span class="cursor-pointer hover:underline" data-ticker="${escapeHTML(alert.ticker)}">$${escapeHTML(alert.ticker)}</span>:
              </span>
              <span class="text-primary">${escapeHTML(description)}</span>
            </div>
            <span class="text-xs ${level.textColor} uppercase font-semibold">${alert.level}</span>
          </div>
        </div>
      `;
    }).join('');
    
    // Click handlers are now handled globally via event delegation in app.ts
    // No need to add individual handlers here
  }).catch(error => {
    console.error('Failed to load momentum detection:', error);
    container.innerHTML = '<p class="text-secondary">Failed to load momentum detection.</p>';
  });
}

function renderBreakoutTickers(breakouts: DashboardResponse['breakout_tickers'], handleTickerClick: (ticker: string) => void): void {
  const container = document.getElementById('breakout-table-container');
  if (!container) return;
  
  const tableId = 'breakout-table-container';
  const currentSort = sortState[tableId] || { column: 'velocity_increase', direction: 'desc' };
  
  // Sort data based on current sort state
  const sorted = [...breakouts].sort((a, b) => {
    const aVal = a[currentSort.column as keyof typeof a] ?? 0;
    const bVal = b[currentSort.column as keyof typeof b] ?? 0;
    
    if (typeof aVal === 'string' || typeof bVal === 'string') {
      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return currentSort.direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    }
    
    // Numeric comparison
    const aNum = Number(aVal) || 0;
    const bNum = Number(bVal) || 0;
    return currentSort.direction === 'asc' ? aNum - bNum : bNum - aNum;
  });
  
  container.innerHTML = createTable(sorted, [
    { header: 'Ticker', key: 'ticker', formatter: (t: string) => `<span class="font-bold text-neon-cyan">${escapeHtml(t)}</span>`, type: 'html', sortable: true },
    { header: 'Velocity', key: 'velocity_increase', formatter: (v: number | undefined) => v != null ? `${Number(v).toFixed(1)}x` : 'N/A', align: 'text-right', sortable: true },
    { header: 'Mentions (1hr)', key: 'recent_count', align: 'text-right', sortable: true },
    { header: 'Mentions (prior 23hr)', key: 'historical_count', align: 'text-right', sortable: true }
  ], handleTickerClick, tableId, () => {
    // Get fresh data from store for re-sorting
    const { data } = useDashboardStore.getState();
    renderBreakoutTickers(data?.breakout_tickers || breakouts, handleTickerClick);
  }, 'w-full min-w-[480px] text-sm');
}

async function applyHypeScoreWindow(
  window: HypeScoreWindow,
  handleTickerClick: (ticker: string) => void
): Promise<void> {
  try {
    const payload = await fetchDashboardHypeScores(window);
    useDashboardStore.getState().setHypeScoreWindow(window);
    useDashboardStore.getState().patchHypeScores(payload.hype_score);
    syncHypeWindowCaption(window);
    renderHypeScore(payload.hype_score, handleTickerClick);
  } catch (error) {
    logError(error, 'DashboardHypeWindow');
    showToast('Failed to load hype scores for that period', true);
  }
}

function syncHypeWindowCaption(window: HypeScoreWindow): void {
  const period = document.getElementById('hype-score-window-period');
  if (!period) {
    return;
  }
  period.textContent = window === '24h' ? '24 hours' : window === '7d' ? '7 days' : '30 days';
}

function syncHypeTabButtons(active: HypeScoreWindow): void {
  document.querySelectorAll<HTMLButtonElement>('.hype-window-tab').forEach((btn) => {
    const w = btn.dataset.hypeWindow as HypeScoreWindow | undefined;
    const on = w === active;
    btn.setAttribute('aria-selected', on ? 'true' : 'false');
    btn.classList.toggle('border-neon-green', on);
    btn.classList.toggle('ring-1', on);
    btn.classList.toggle('ring-neon-green/40', on);
    btn.classList.toggle('text-neon-green', on);
    btn.classList.toggle('text-gray-200', !on);
  });
}

function renderSentimentShift(shifts: DashboardResponse['sentiment_shift'], handleTickerClick: (ticker: string) => void): void {
  const container = document.getElementById('sentiment-shift-table-container');
  if (!container) return;
  
  const tableId = 'sentiment-shift-table-container';
  const currentSort = sortState[tableId] || { column: 'sentiment_change', direction: 'desc' };
  
  // Sort data based on current sort state
  const sorted = [...shifts].sort((a, b) => {
    const aVal = a[currentSort.column as keyof typeof a] ?? 0;
    const bVal = b[currentSort.column as keyof typeof b] ?? 0;
    
    if (typeof aVal === 'string' || typeof bVal === 'string') {
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return currentSort.direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    }
    
    const aNum = Number(aVal) || 0;
    const bNum = Number(bVal) || 0;
    return currentSort.direction === 'asc' ? aNum - bNum : bNum - aNum;
  });
  
  const sentimentBadge = (v: number | undefined): string => {
    if (v == null || isNaN(v)) return 'N/A';
    if (v >= 0.05) {
      return `<span class="text-green-400 font-semibold">${v.toFixed(2)} Bullish</span>`;
    }
    if (v <= -0.05) {
      return `<span class="text-red-400 font-semibold">${v.toFixed(2)} Bearish</span>`;
    }
    return `<span class="text-gray-400 font-semibold">${v.toFixed(2)} Neutral</span>`;
  };

  const sentimentChangeWithArrow = (v: number | undefined): string => {
    if (v == null || isNaN(v)) return 'N/A';
    if (v > 0) {
      return `<span class="text-green-400 font-semibold">▲ +${v.toFixed(2)}</span>`;
    }
    if (v < 0) {
      return `<span class="text-red-400 font-semibold">▼ ${v.toFixed(2)}</span>`;
    }
    return `<span class="text-gray-400 font-semibold">→ ${v.toFixed(2)}</span>`;
  };

  const tableHtml = createTable(sorted, [
    { header: 'Ticker', key: 'ticker', formatter: (t: string) => `<span class="font-bold text-neon-cyan">${escapeHtml(t)}</span>`, type: 'html', sortable: true },
    { header: 'Current', key: 'avg_sent_current', formatter: sentimentBadge, type: 'html', align: 'text-right', sortable: true },
    { header: 'Previous', key: 'avg_sent_prev', formatter: sentimentBadge, type: 'html', align: 'text-right', sortable: true },
    { header: 'Change', key: 'sentiment_change', formatter: sentimentChangeWithArrow, type: 'html', align: 'text-right', sortable: true }
  ], handleTickerClick, tableId, () => {
    // Get fresh data from store for re-sorting
    const { data } = useDashboardStore.getState();
    renderSentimentShift(data?.sentiment_shift || shifts, handleTickerClick);
  }, 'w-full min-w-[480px] text-sm');
  
  container.innerHTML = tableHtml;
}

function renderHypeScore(scores: DashboardResponse['hype_score'], handleTickerClick: (ticker: string) => void): void {
  const container = document.getElementById('hype-score-table-container');
  if (!container) return;

  const activeWindow = useDashboardStore.getState().hypeScoreWindow;
  syncHypeWindowCaption(activeWindow);
  syncHypeTabButtons(activeWindow);
  
  // Get or create filter container
  let filterContainer = document.getElementById('hype-score-filter-container');
  if (!filterContainer) {
    const card = container.closest('.card');
    if (card) {
      filterContainer = document.createElement('div');
      filterContainer.id = 'hype-score-filter-container';
      filterContainer.className = 'mb-4 flex flex-col gap-3';
      filterContainer.innerHTML = `
        <div class="flex flex-wrap gap-2 items-center" role="tablist" aria-label="Hype score time window">
          <span class="text-sm text-gray-400 mr-1">Period</span>
          <button type="button" role="tab" class="hype-window-tab px-3 py-1.5 rounded-md text-sm font-medium bg-gray-800 border border-gray-600 hover:border-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan/50" data-hype-window="24h" aria-selected="true">24 hours</button>
          <button type="button" role="tab" class="hype-window-tab px-3 py-1.5 rounded-md text-sm font-medium bg-gray-800 border border-gray-600 hover:border-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan/50" data-hype-window="7d" aria-selected="false">1 week</button>
          <button type="button" role="tab" class="hype-window-tab px-3 py-1.5 rounded-md text-sm font-medium bg-gray-800 border border-gray-600 hover:border-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan/50" data-hype-window="30d" aria-selected="false">1 month</button>
        </div>
        <div class="flex flex-wrap gap-4 items-center">
        <div class="flex items-center gap-2">
          <label for="price-filter-min" class="text-sm text-gray-300">Min Price:</label>
          <input type="number" id="price-filter-min" step="0.01" min="0" placeholder="0.00" 
                 class="w-24 p-2 rounded-md bg-gray-800 text-white border border-gray-700 focus:border-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan">
        </div>
        <div class="flex items-center gap-2">
          <label for="price-filter-max" class="text-sm text-gray-300">Max Price:</label>
          <input type="number" id="price-filter-max" step="0.01" min="0" placeholder="No limit" 
                 class="w-24 p-2 rounded-md bg-gray-800 text-white border border-gray-700 focus:border-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan">
        </div>
        <button id="clear-price-filter" class="px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors text-sm">
          Clear Filter
        </button>
        </div>
      `;
      card.insertBefore(filterContainer, container);
      filterContainer.addEventListener('click', (ev) => {
        const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('[data-hype-window]');
        if (!btn?.dataset.hypeWindow) {
          return;
        }
        const w = btn.dataset.hypeWindow as HypeScoreWindow;
        if (w !== '24h' && w !== '7d' && w !== '30d') {
          return;
        }
        if (w === useDashboardStore.getState().hypeScoreWindow) {
          return;
        }
        void applyHypeScoreWindow(w, handleTickerClick);
      });
    }
  }
  
  // Apply price filter
  const minPriceInput = document.getElementById('price-filter-min') as HTMLInputElement;
  const maxPriceInput = document.getElementById('price-filter-max') as HTMLInputElement;
  const clearFilterBtn = document.getElementById('clear-price-filter');
  const { hypeMinPrice, hypeMaxPrice } = useFilterStore.getState();
  if (minPriceInput) {
    minPriceInput.value = hypeMinPrice == null ? '' : String(hypeMinPrice);
  }
  if (maxPriceInput) {
    maxPriceInput.value = hypeMaxPrice == null ? '' : String(hypeMaxPrice);
  }

  const minPrice = hypeMinPrice;
  const maxPrice = hypeMaxPrice;
  
  let filteredScores = scores;
  if (minPrice !== null || maxPrice !== null) {
    filteredScores = scores.filter(score => {
      const price = score.price;
      if (price === null || price === undefined) return false;
      if (minPrice !== null && price < minPrice) return false;
      if (maxPrice !== null && price > maxPrice) return false;
      return true;
    });
  }
  
  // Set up filter event listeners
  if (minPriceInput && maxPriceInput) {
    const applyFilter = () => {
      const nextMin = minPriceInput.value ? parseFloat(minPriceInput.value) : null;
      const nextMax = maxPriceInput.value ? parseFloat(maxPriceInput.value) : null;
      useFilterStore.getState().setHypePriceRange(nextMin, nextMax);
      const { data } = useDashboardStore.getState();
      renderHypeScore(data?.hype_score || scores, handleTickerClick);
    };
    
    minPriceInput.oninput = applyFilter;
    maxPriceInput.oninput = applyFilter;
    
    if (clearFilterBtn) {
      clearFilterBtn.onclick = () => {
        minPriceInput.value = '';
        maxPriceInput.value = '';
        useFilterStore.getState().clearHypePriceRange();
        const { data } = useDashboardStore.getState();
        renderHypeScore(data?.hype_score || scores, handleTickerClick);
      };
    }
  }
  
  const tableId = 'hype-score-table-container';
  const currentSort = sortState[tableId] || { column: 'hype_score', direction: 'desc' };
  
  // Sort data based on current sort state
  const sorted = [...filteredScores].sort((a, b) => {
    const aVal = a[currentSort.column as keyof typeof a] ?? 0;
    const bVal = b[currentSort.column as keyof typeof b] ?? 0;
    
    if (typeof aVal === 'string' || typeof bVal === 'string') {
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return currentSort.direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    }
    
    const aNum = Number(aVal) || 0;
    const bNum = Number(bVal) || 0;
    return currentSort.direction === 'asc' ? aNum - bNum : bNum - aNum;
  });
  
  const tableHtml = createTable(sorted, [
    { header: 'Ticker', key: 'ticker', formatter: (t: string) => `<span class="font-bold text-neon-cyan">${escapeHtml(t)}</span>`, type: 'html', sortable: true },
    {
      header: 'Hype Score',
      key: 'hype_score',
      formatter: (v: number | undefined | null) => {
        if (v == null || isNaN(v)) return 'N/A';
        const color = v >= 70 ? 'text-green-400' : v >= 40 ? 'text-yellow-400' : 'text-red-400';
        return `<span class="${color} font-semibold">${v.toFixed(2)}</span>`;
      },
      type: 'html',
      align: 'text-right',
      sortable: true
    },
    { header: 'Mentions', key: 'Mentions', align: 'text-right', sortable: true },
    { header: 'Unique Authors', key: 'unique_authors', align: 'text-right', sortable: true },
    { header: 'Author Diversity', key: 'author_diversity', formatter: (v: number | undefined | null) => (v != null && !isNaN(v)) ? v.toFixed(2) : 'N/A', align: 'text-right', sortable: true },
    { header: 'Price', key: 'price', formatter: (v: number | null | undefined) => (v != null && !isNaN(v)) ? formatPrice(v) : 'N/A', align: 'text-right', sortable: true },
    { header: 'Avg Sentiment', key: 'avg_sentiment', formatter: (v: number | undefined | null) => (v != null && !isNaN(v)) ? formatSentiment(v) : 'N/A', align: 'text-right', sortable: true }
  ], handleTickerClick, tableId, () => {
    // Get fresh data from store for re-sorting
    const { data } = useDashboardStore.getState();
    renderHypeScore(data?.hype_score || scores, handleTickerClick);
  }, 'w-full min-w-[600px] text-sm');
  
  container.innerHTML = tableHtml;
}

function renderRecentMentions(_mentions: DashboardResponse['recent_mentions']): void {
  // This will be handled by the momentum detection system
  // For now, just update the store
}

// Helper functions
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function formatTimestamp(timestamp: string | Date): string {
  try {
    // Ensure we're working with a Date object
    let date: Date;
    if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      // Parse as UTC if it's a string (API sends UTC timestamps)
      const timestampStr = String(timestamp);
      if (timestampStr.includes('Z') || timestampStr.includes('+00:00')) {
        date = new Date(timestampStr);
      } else {
        // Assume UTC if no timezone specified
        date = new Date(timestampStr + (timestampStr.includes('T') ? 'Z' : ''));
      }
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid timestamp:', timestamp, 'parsed as:', date);
      return String(timestamp);
    }
    
    // Format in EST/EDT (America/New_York)
    // Use Intl.DateTimeFormat for reliable timezone conversion
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
    
    return formatter.format(date);
  } catch (error) {
    console.error('Error formatting timestamp:', error, timestamp);
    return String(timestamp);
  }
}

function formatSentiment(sentiment: number | undefined | null): string {
  if (sentiment == null || isNaN(sentiment)) return 'N/A';
  return sentiment.toFixed(2);
}

function formatPercent(percent: number | undefined | null): string {
  if (percent == null || isNaN(percent)) return 'N/A';
  const sign = percent >= 0 ? '+' : '';
  return `${sign}${percent.toFixed(2)}%`;
}

function formatLargeNumber(num: number | undefined | null): string {
  if (num == null || isNaN(num)) return 'N/A';
  if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
  return num.toFixed(0);
}

interface TableColumn {
  header: string;
  key: string;
  formatter?: (value: any) => string;
  type?: 'html' | 'text';
  align?: 'text-left' | 'text-right' | 'text-center';
  sortable?: boolean;
}

function createTable(
  data: any[], 
  columns: TableColumn[], 
  handleClick?: (ticker: string) => void,
  tableId?: string,
  onSort?: () => void,
  tableClass = 'w-full text-sm'
): string {
  if (data.length === 0) {
    return '<p class="text-secondary">No data available.</p>';
  }
  
  const currentSort = tableId ? sortState[tableId] : null;
  
  // Use the same alignment for headers as their corresponding data cells
  const headers = columns.map((col) => {
    const align = col.align || 'text-left';
    const isSortable = col.sortable === true;
    const isSorted = currentSort && currentSort.column === col.key;
    const sortDirection = isSorted ? currentSort.direction : null;
    
    // Sort indicator arrow
    let sortIndicator = '';
    if (isSorted && sortDirection) {
      sortIndicator = sortDirection === 'asc' 
        ? ' <span class="text-neon-cyan">↑</span>' 
        : ' <span class="text-neon-cyan">↓</span>';
    } else if (isSortable) {
      sortIndicator = ' <span class="text-secondary opacity-50">↕</span>';
    }
    
    const clickableClass = isSortable ? 'cursor-pointer hover:bg-gray-700 select-none' : '';
    const sortDataAttr = isSortable ? `data-sort-key="${col.key}" data-table-id="${tableId || ''}"` : '';
    
    return `<th class="${align} p-2 ${clickableClass}" ${sortDataAttr}>${col.header}${sortIndicator}</th>`;
  }).join('');
  
  const rows = data.map(row => {
    const cells = columns.map(col => {
      const value = row[col.key];
      const formatted = col.formatter ? col.formatter(value) : String(value);
      const align = col.align || 'text-left';
      const ticker = col.key === 'ticker' ? String(value) : '';
      const clickable = col.key === 'ticker' && handleClick;
      return `<td class="${align} p-2 ${clickable ? 'cursor-pointer hover:underline' : ''}" ${clickable ? `data-ticker="${escapeHtml(ticker)}"` : ''}>${col.type === 'html' ? formatted : escapeHtml(formatted)}</td>`;
    }).join('');
    return `<tr>${cells}</tr>`;
  }).join('');
  
  const tableHtml = `
    <table class="${tableClass}">
      <thead>
        <tr class="border-b border-primary">${headers}</tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
  
  // Set up sort handlers after rendering
  if (tableId && onSort) {
    setTimeout(() => {
      const table = document.querySelector(`#${tableId} table`);
      if (table) {
        table.querySelectorAll('th[data-sort-key]').forEach(header => {
          header.addEventListener('click', () => {
            const sortKey = header.getAttribute('data-sort-key');
            const tableIdForSort = header.getAttribute('data-table-id');
            if (sortKey && tableIdForSort) {
              const current = sortState[tableIdForSort];
              if (current && current.column === sortKey) {
                // Toggle direction
                sortState[tableIdForSort] = {
                  column: sortKey,
                  direction: current.direction === 'asc' ? 'desc' : 'asc'
                };
              } else {
                // New column, default to descending
                sortState[tableIdForSort] = {
                  column: sortKey,
                  direction: 'desc'
                };
              }
              onSort();
            }
          });
        });
      }
    }, 0);
  }
  
  // Click handlers for tickers are now handled globally via event delegation in app.ts
  
  return tableHtml;
}

